import React, { useCallback, useEffect, useState } from 'react';
import pluralize from 'pluralize';
import { Button, Dropdown, Menu, Modal, Spin, Table } from 'antd';
import cx from 'classnames';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { isNil } from 'lodash';

import { userSelector } from 'redux/selectors/authenticationSelectors';
import {
  deleteRegisteredTeammate,
  deleteUnregisteredTeammate,
  getMyTeammates,
  sendInviteUser,
  teammateActivation,
  teammateDeactivation,
} from 'redux/reducers/recruiterSlice';
import { pageSmallSizeTable } from 'constants/index';
import { getFullName } from 'utils/personUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import InviteUserModal from 'components/modals/InviteUserModal';
import EditTeammateModal from '../EditTeammateModal';
import BaseLabel from 'components/wrappers/BaseLabel';
import {
  getTeammateStatusLabel,
  getTeammateRoleLabel,
} from 'utils/teammateUtils';
import {
  TeammateModel,
  TeammateModelPagedResponseModel,
  TeammateStatusEnum,
} from 'api/backofficeApiClient';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';

import styles from './MyTeammatesTable.module.less';

const { Item } = Menu;

const getCorrectStatusStyle = (status: TeammateStatusEnum) => {
  switch (status) {
    case TeammateStatusEnum.Pending:
      return styles.pending;
    case TeammateStatusEnum.Active:
      return styles.registered;
    case TeammateStatusEnum.InActive:
      return styles.inactive;
  }
};

const getMessageOnDelete = (status: TeammateStatusEnum) => {
  switch (status) {
    case TeammateStatusEnum.Pending:
      return "If you delete this teammate, they won't be able to register by the previously sent invitation link. Do you wish to proceed?";
    case TeammateStatusEnum.Active:
    case TeammateStatusEnum.InActive:
      return 'The teammate will be permanently deleted. Are you sure?';
  }
};

export default function MyTeammatesTable() {
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [teammateDetails, setTeammateDetails] =
    useState<TeammateModel | null>();
  const [teammatesData, setTeammatesData] =
    useState<TeammateModelPagedResponseModel>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const [isTeammatesDataLoading, setIsTeammatesDataLoading] = useState(false);
  const user = useAppSelector(userSelector);
  const dispatch = useAppDispatch();

  const teammatesCountMessage = pluralize(
    'User',
    teammatesData?.totalCount || 0,
    true,
  );

  const resetTable = () => {
    if (!teammatesData?.details) return;
    if (currentTablePage === 1 || teammatesData?.details.length > 1) {
      loadTeammatesDetails();
    } else {
      setCurrentTablePage((currentPage) => currentPage - 1);
    }
  };

  const loadTeammatesDetails = useCallback(async () => {
    setIsTeammatesDataLoading(true);

    const result = await dispatch(getMyTeammates(currentTablePage - 1));

    if (getMyTeammates.fulfilled.match(result)) {
      setTeammatesData(result.payload);
    } else {
      setTeammatesData({
        totalCount: 0,
        details: [],
      });
    }

    setIsTeammatesDataLoading(false);
    // eslint-disable-next-line
  }, [dispatch, currentTablePage]);

  const loadInitialData = useCallback(async () => {
    await loadTeammatesDetails();

    setIsInitialDataLoaded(true);
  }, [loadTeammatesDetails]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleSendInvite = async (email: string) => {
    const result = await dispatch(sendInviteUser(email));

    if (sendInviteUser.fulfilled.match(result)) {
      BaseAlert.success('The invitation has been resent.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleResendInvitation = (email: string) => {
    return Modal.confirm({
      title: 'Do you really want to send the invitation once again?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await handleSendInvite(email);
      },
      onCancel() {},
    });
  };

  const handleTeammateDeactivation = async (teammateId: string) => {
    const result = await dispatch(teammateDeactivation(teammateId));

    if (teammateDeactivation.fulfilled.match(result)) {
      BaseAlert.success('Teammate has been deactivated successfully.');
      resetTable();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleTeammateActivation = async (teammateId: string) => {
    const result = await dispatch(teammateActivation(teammateId));

    if (teammateActivation.fulfilled.match(result)) {
      BaseAlert.success('Teammate has been activated successfully.');
      resetTable();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const getTeammateDeactivateConfirmationModal = (teammateId: string) => {
    return Modal.confirm({
      title:
        "If you deactivate this teammate, they won't be able to login unless you reactivate them. Do you want to proceed?",
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await handleTeammateDeactivation(teammateId);
      },
      onCancel() {},
    });
  };

  const getTeammateActivateConfirmationModal = (teammateId: string) => {
    return Modal.confirm({
      title:
        'If you activate this teammate, the account access will be reinstated and they will be able to sign in. Do you want to proceed?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await handleTeammateActivation(teammateId);
      },
      onCancel() {},
    });
  };

  const renderMenu = (teammate: TeammateModel) => (
    <Menu className={styles.dropdown}>
      {teammate.status === TeammateStatusEnum.Active && (
        <Item key="deactivate">
          <Button
            className="borderless-btn"
            icon={<span className="icon icon-inactive" />}
            onClick={() =>
              getTeammateDeactivateConfirmationModal(teammate.userId || '')
            }
          >
            Deactivate
          </Button>
        </Item>
      )}
      {teammate.status === TeammateStatusEnum.InActive && (
        <Item key="activate">
          <Button
            className="borderless-btn"
            icon={<span className="icon icon-active" />}
            onClick={() =>
              getTeammateActivateConfirmationModal(teammate.userId || '')
            }
          >
            Activate
          </Button>
        </Item>
      )}
      {teammate.status === TeammateStatusEnum.Pending && (
        <>
          {!isNil(teammate.invitationLink) && (
            <Item key="invitationLink">
              <CopyToClipboard
                text={teammate.invitationLink}
                onCopy={handleCopyLinkToClipboard}
              >
                <Button
                  className="borderless-btn"
                  icon={<span className="icon icon-type" />}
                >
                  Copy Link
                </Button>
              </CopyToClipboard>
            </Item>
          )}
          <Item key="resend">
            <Button
              className="borderless-btn"
              onClick={() => handleResendInvitation(teammate.email || '')}
              icon={<span className="icon icon-resend" />}
            >
              Resend
            </Button>
          </Item>
        </>
      )}

      {teammate.status !== TeammateStatusEnum.Pending && (
        <Item key="edit">
          <Button
            className="borderless-btn"
            onClick={() => handleViewTeammateModal(teammate)}
            icon={<span className="icon icon-edit" />}
          >
            Edit
          </Button>
        </Item>
      )}
      <Item key="delete">
        <Button
          className="borderless-btn"
          onClick={() => handleDelete(teammate)}
          icon={<span className="icon icon-delete" />}
        >
          Delete
        </Button>
      </Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Teammate Name',
      key: 'teammateName',
      render: ({ firstName, lastName }: TeammateModel) => (
        <span className="wrapped-text">{getFullName(firstName, lastName)}</span>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      render: ({ email }: TeammateModel) => (
        <span className="wrapped-text">{email}</span>
      ),
    },
    {
      title: 'Role',
      key: 'Role',
      render: ({ role }: TeammateModel) => getTeammateRoleLabel(role),
    },
    {
      title: 'Status',
      key: 'Status',
      render: (teammate: TeammateModel) =>
        !isNil(teammate.status) && (
          <BaseLabel
            status={getTeammateStatusLabel(teammate.status)}
            className={cx(
              getCorrectStatusStyle(teammate.status),
              styles.status,
            )}
          />
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (teammate: TeammateModel) => (
        <Dropdown overlay={renderMenu(teammate)} trigger={['click']}>
          <Button>
            <i className="icon icon-dots" />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const handleCopyLinkToClipboard = (text: string, result: boolean) => {
    if (result) {
      return BaseAlert.success(
        'Invitation link has been copied to the clipboard.',
      );
    } else {
      return BaseAlert.error('Something went wrong, please try again.');
    }
  };

  const handleDelete = (teammate: TeammateModel) => {
    Modal.confirm({
      title: getMessageOnDelete(teammate.status as TeammateStatusEnum),
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await onDeleteTeammate(teammate);
      },
      onCancel() {},
    });
  };

  const onDeleteTeammate = async (teammate: TeammateModel) => {
    const correctAction =
      teammate.status === TeammateStatusEnum.Pending
        ? deleteUnregisteredTeammate
        : deleteRegisteredTeammate;

    const correctData =
      teammate.status === TeammateStatusEnum.Pending
        ? teammate.email || ''
        : teammate.userId || '';

    const result = await dispatch(correctAction(correctData));

    if (correctAction.fulfilled.match(result)) {
      BaseAlert.success('Teammate has been deleted successfully.');
      resetTable();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleViewInviteUserModal = () => {
    setIsInviteUserModalOpen(true);
  };

  const handleCloseInviteUserModal = () => {
    setIsInviteUserModalOpen(false);
  };

  const handleViewTeammateModal = (initialValues: TeammateModel) => {
    setTeammateDetails(initialValues);
  };

  const handleCloseTeammateModal = () => {
    setTeammateDetails(null);
  };

  return (
    <>
      {!isInitialDataLoaded && <Spin />}
      {isInitialDataLoaded && (
        <>
          {teammateDetails && (
            <EditTeammateModal
              onSuccess={loadTeammatesDetails}
              teammateDetails={teammateDetails}
              onClose={handleCloseTeammateModal}
            />
          )}
          {isInviteUserModalOpen && (
            <InviteUserModal
              onSuccess={loadTeammatesDetails}
              onClose={handleCloseInviteUserModal}
            />
          )}
          <BaseTableHeader
            title="My Teammates"
            enableSubTitle={isInitialDataLoaded}
            subTitle={teammatesCountMessage}
            isShowActions={user?.isAccountSettingsComplete}
          >
            <div>
              <Button
                type="primary"
                className="mobile-default"
                onClick={handleViewInviteUserModal}
                data-id="inviteTeammateBtn"
              >
                Invite Teammate
              </Button>
            </div>
          </BaseTableHeader>
          <Table
            showSorterTooltip={false}
            className={cx('custom-table', styles.table)}
            pagination={{
              total: teammatesData?.totalCount,
              pageSize: pageSmallSizeTable,
              onChange: setCurrentTablePage,
              current: currentTablePage,
              showSizeChanger: false,
            }}
            columns={columns}
            dataSource={teammatesData?.details}
            rowKey={(record) => record?.email as string}
            loading={isTeammatesDataLoading}
            locale={{
              emptyText: 'You currently do not have any invited teammates.',
            }}
          />
        </>
      )}
    </>
  );
}
